import * as React from 'react';

import { Button, Col, ContentCard, Row } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useScaleLocalClient } from '~/shared/providers/libp2p/useScaleLocalClient';

type Props = {
  scaleId: string;
  sidecarId: string;
  sidecarDetail: {
    id: string;
    name: string;
    peerId: string | null;
    multiaddrs: string[];
    status: string;
  } | null;
};

type LiveWeight = {
  grossWeight: number;
  tareWeight: number;
  netWeight: number;
  unitOfMeasurement: number;
  timestampMs: bigint;
  stable: boolean;
};

export const ScaleTabWeight: React.FC<Props> = ({
  scaleId,
  sidecarId,
  sidecarDetail,
}) => {
  const t = useTranslations();
  const scaleClient = useScaleLocalClient(sidecarId);
  const { connection, isConnecting } = useHardwareSidecarConnection({
    sidecar: sidecarDetail,
  });
  const [weight, setWeight] = React.useState<LiveWeight | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const fetchCurrentWeight = React.useCallback(async () => {
    if (!scaleClient) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await scaleClient.getCurrentWeight({ scaleId });
      if (response.weight) {
        setWeight(response.weight as LiveWeight);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t.hardwareView.scaleDetailView.messages.weightReadFailed,
      );
    } finally {
      setIsLoading(false);
    }
  }, [scaleClient, scaleId, t]);

  React.useEffect(() => {
    if (!scaleClient || !connection?.connected) return;

    let cancelled = false;
    const abort = new AbortController();

    fetchCurrentWeight();

    (async () => {
      try {
        for await (const update of scaleClient.subscribeWeight(
          { scaleId },
          { signal: abort.signal },
        )) {
          if (cancelled) return;
          setWeight(update as LiveWeight);
          setError(null);
        }
      } catch (err) {
        if (
          !cancelled &&
          !(err instanceof DOMException && err.name === 'AbortError')
        ) {
          setError(
            err instanceof Error
              ? err.message
              : t.hardwareView.scaleDetailView.messages.weightStreamFailed,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [scaleClient, connection?.connected, scaleId, fetchCurrentWeight, t]);

  if (isConnecting) {
    return (
      <div className="p-6 text-neutral/60">
        {t.hardwareView.scaleDetailView.messages.connecting}
      </div>
    );
  }

  if (!connection?.connected || !scaleClient) {
    return (
      <div className="p-6 text-neutral/60">
        {t.hardwareView.scaleDetailView.messages.offline}
      </div>
    );
  }

  return (
    <div className="@ScaleTabWeight flex h-full flex-col gap-4 p-5">
      <Row gap2>
        <Button
          variant="outline"
          onPress={fetchCurrentWeight}
          isDisabled={isLoading}
        >
          {t.hardwareView.scaleDetailView.actions.refreshWeight}
        </Button>
      </Row>
      {error && (
        <div className="rounded bg-failure/10 p-2 text-sm text-failure">
          {error}
        </div>
      )}
      <ContentCard className="p-6">
        <Col gap3>
          <div className="text-sm text-neutral/60">
            {t.hardwareView.scaleDetailView.sections.liveWeight}
          </div>
          <div className="text-4xl font-semibold text-neutral">
            {weight
              ? `${weight.netWeight} ${weight.unitOfMeasurement || ''}`.trim()
              : t.hardwareView.scaleDetailView.messages.noWeightYet}
          </div>
          <Row gap6 className="flex-wrap text-sm">
            <div>
              {t.hardwareView.scaleDetailView.fields.grossWeight}:{' '}
              {weight?.grossWeight ?? '\u2013'}
            </div>
            <div>
              {t.hardwareView.scaleDetailView.fields.tareWeight}:{' '}
              {weight?.tareWeight ?? '\u2013'}
            </div>
            <div>
              {t.hardwareView.scaleDetailView.fields.netWeight}:{' '}
              {weight?.netWeight ?? '\u2013'}
            </div>
            <div>
              {t.hardwareView.scaleDetailView.fields.stability}:{' '}
              {weight?.stable
                ? t.hardwareView.scaleDetailView.labels.stable
                : t.hardwareView.scaleDetailView.labels.unstable}
            </div>
          </Row>
        </Col>
      </ContentCard>
    </div>
  );
};
